import React, {useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {FileUpload} from "primereact/fileupload";
import {Message} from "primereact/message";

import {AppContext} from "../../HomePage";

// A card that opens a popup to download an Excel template and run one or more
// upload actions. Reused across onboarding steps for the "From Excel" ops.
//
// Props:
//  - icon, color, title, hint, danger
//  - download: optional () => Promise (already bound to template + exportFile)
//  - uploads: [{ labelKey, summaryKey, run: (file) => Promise }]
export const ExcelOpsCard = ({t, icon, color, title, hint, danger, download, uploads = []}) => {
    const {toast, setError} = React.useContext(AppContext);
    const [show, setShow] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [busyKey, setBusyKey] = useState(null);

    const doDownload = () => {
        setDownloading(true);
        Promise.resolve(download()).catch(error => { setError(error); setError(null); }).finally(() => setDownloading(false));
    };

    const makeHandler = (u) => ({files}) => {
        const [file] = files;
        // No options.clear(): in basic mode the input is already gone while the handler
        // runs, and the busy flag swaps the FileUpload for a spinner, so it remounts
        // empty anyway. Calling clear() threw "Cannot set properties of null" - after
        // the upload (an error toast on a successful import) or before it (no upload).
        setBusyKey(u.labelKey);
        Promise.resolve(u.run(file)).then((result) => {
            let severity = 'success';
            let detail = t('message_detail.successful');
            if (u.summary) {
                const s = u.summary(result);
                if (typeof s === 'string') detail = s;
                else if (s) { detail = s.detail || detail; severity = s.severity || severity; }
            }
            toast.current.show({severity, summary: t(u.summaryKey || u.labelKey), detail});
        }).catch(error => { setError(error); setError(null); }).finally(() => setBusyKey(null));
    };

    return (
        <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', display: 'flex', flexDirection: 'column', gap: 8}}>
            <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                <i className={icon} style={{color: color || (danger ? '#dc2626' : '#15803d')}}/> {title}
            </div>
            {hint && <small style={{color: '#6b7280', flex: 1}}>{hint}</small>}
            <Button label={title} icon={icon}
                    className={'p-button-outlined ' + (danger ? 'p-button-danger' : 'p-button-success')}
                    onClick={() => setShow(true)}/>

            <Dialog header={title} visible={show} style={{width: '560px', maxWidth: '95vw'}} modal
                    onHide={() => !busyKey && setShow(false)}
                    footer={<Button label={t('actions.close')} className="p-button-text" disabled={!!busyKey} onClick={() => setShow(false)}/>}>
                {hint && <small style={{display: 'block', color: '#6b7280', marginBottom: '1em'}}>{hint}</small>}
                {danger && <Message severity="warn" text={t('onboarding.cleanup.irreversible')} style={{display: 'block', marginBottom: '1em'}}/>}
                <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                    {download && (
                        <div>
                            <div style={{fontWeight: 600, marginBottom: '.35em'}}>{t('actions.download_template')}</div>
                            <Button label={t('actions.download_template')} icon="pi pi-download"
                                    className="p-button-outlined p-button-success" loading={downloading} onClick={doDownload}/>
                        </div>
                    )}
                    {uploads.map(u => (
                        <div key={u.labelKey}>
                            <div style={{fontWeight: 600, marginBottom: '.35em'}}>{t(u.labelKey)}</div>
                            {busyKey === u.labelKey
                                ? <Button label={t(u.labelKey)} icon="pi pi-spin pi-spinner" disabled/>
                                : <FileUpload customUpload mode="basic" auto accept=".xlsx,.xls"
                                              chooseLabel={t(u.labelKey)} uploadHandler={makeHandler(u)}/>}
                        </div>
                    ))}
                </div>
            </Dialog>
        </div>
    );
};

export default ExcelOpsCard;
